/** Stable cache key for a blob/CDN asset (ignores SAS query params). */
export function extractBlobAssetKey(url: string): string {
  if (!url) return url;
  const normalized = normalizeMediaFetchUrl(url);
  try {
    const parsed = new URL(normalized);
    const path = parsed.pathname.replace(/^\/+/, '');
    if (path) return path;
  } catch {
    // fall through
  }
  return normalized.split('?')[0] ?? normalized;
}

/** Decode HTML entities in signed CDN URLs before fetch. */
export function normalizeMediaFetchUrl(url: string): string {
  if (!url) return url;
  return url.replace(/&amp;/gi, '&').replace(/&#0*38;/g, '&');
}

const CDN_HOST = import.meta.env.VITE_MEDIA_CDN_HOST as string | undefined;
const CDN_PROXY_PREFIX = (import.meta.env.VITE_MEDIA_CDN_PROXY_PREFIX as string | undefined) ?? '/__media-cdn';

/**
 * URL used for fetch / pdf.js. In dev, routes Azure Front Door through the Vite proxy
 * so blob prefetch works without per-origin CDN CORS.
 */
export function resolveMediaFetchUrl(url: string): string {
  const normalized = normalizeMediaFetchUrl(url);
  if (!import.meta.env.DEV || !CDN_HOST?.trim()) {
    return normalized;
  }
  try {
    const parsed = new URL(normalized);
    if (parsed.hostname === CDN_HOST.trim()) {
      return `${CDN_PROXY_PREFIX}${parsed.pathname}${parsed.search}`;
    }
  } catch {
    // fall through
  }
  return normalized;
}

type PreviewMediaFingerprintLayer = {
  layerId: number;
  componentId: string;
  itemId?: string;
  data?: Record<string, string | undefined> | null;
};

/** Stable identity of preview media (blob path, not SAS query). Used to skip still-frame remounts. */
export function previewLayersMediaFingerprint(layers: PreviewMediaFingerprintLayer[]): string {
  return layers
    .map((layer) => {
      const data = layer.data ?? {};
      const primary = extractBlobAssetKey(data.videoUrl ?? data.url ?? data.src ?? data.pdfUrl ?? '');
      const sequenceRaw = data.imageUrls ?? data.slidePreviewUrls ?? data.imageSequenceUrls ?? '';
      let sequence = sequenceRaw;
      if (sequenceRaw.startsWith('[')) {
        try {
          const parsed = JSON.parse(sequenceRaw) as unknown;
          if (Array.isArray(parsed)) {
            sequence = parsed.map((url) => (typeof url === 'string' ? extractBlobAssetKey(url) : '')).join(',');
          }
        } catch {
          sequence = extractBlobAssetKey(sequenceRaw);
        }
      } else if (sequenceRaw) {
        sequence = extractBlobAssetKey(sequenceRaw);
      }
      return [layer.layerId, layer.componentId, layer.itemId ?? '', primary, sequence].join(':');
    })
    .join('|');
}
